import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { History, ChevronDown } from "lucide-react";
import { api } from "@/lib/api";
import { MmaBouts, MmaHeader, useMmaCard } from "@/components/MmaCard";

const LEAGUES = ["NFL", "NBA", "NCAAF", "NCAAB", "NHL", "MLS", "MMA"];

function periodLabel(league, i) {
  const reg = { NHL: 3, MLS: 2 }[league] || 4;
  if (i < reg) return league === "MLS" ? `${i + 1}H` : i + 1;
  return i === reg ? (league === "MLS" ? "ET" : "OT") : "SO";
}

function MmaResults() {
  const card = useMmaCard("results", true);
  if (card === undefined) return <p className="text-xs text-slate-500 py-3">Loading…</p>;
  if (!card) return <p className="text-xs text-slate-500 py-3" data-testid="recent-empty">No recent UFC results.</p>;
  return <div><MmaHeader card={card} /><MmaBouts card={card} compact /></div>;
}

function Side({ t }) {
  return (
    <div className={`flex items-center gap-2 ${t.winner ? "text-white" : "text-slate-500"}`}>
      {t.logo && <img src={t.logo} alt="" className="w-4 h-4 object-contain" loading="lazy" />}
      <span className="text-[11px] font-bold w-9">{t.abbr}</span>
      {t.record && <span className="text-[9px] text-slate-500 font-mono-num">{t.record}</span>}
      <span className="font-mono-num text-xs font-bold ml-auto">{t.score}</span>
    </div>
  );
}

function TopPerformers({ g }) {
  const [leaders, setLeaders] = useState(null);
  useEffect(() => {
    api.get("/game-leaders", { params: { league: g.league, game_id: g.id } }).then((r) => setLeaders(r.data.leaders)).catch(() => setLeaders([]));
  }, [g.league, g.id]);
  if (!leaders) return <div className="text-slate-600">Loading top performers…</div>;
  if (!leaders.length) return null;
  return (
    <div className="space-y-1" data-testid={`top-performers-${g.id}`}>
      <div className="text-smasher-gold font-bold">TOP PERFORMERS</div>
      {leaders.map((l) => (
        <div key={l.cat} className="flex items-center gap-2">
          {l.headshot ? <img src={l.headshot} alt="" className="w-5 h-5 rounded-full object-cover bg-white/10" loading="lazy" /> : <span className="w-5 h-5 rounded-full bg-white/10" />}
          <span className="text-white font-bold truncate">{l.player}</span>
          <span className="text-slate-500">{l.team}</span>
          <span className="ml-auto text-right text-slate-300 truncate max-w-[45%]"><span className="text-slate-500">{l.cat}:</span> {l.value}</span>
        </div>
      ))}
    </div>
  );
}

function Detail({ g }) {
  const periods = g.home.line.length;
  return (
    <div className="mt-2 pt-2 border-t border-white/5 text-[10px] text-slate-400 space-y-1.5" data-testid={`recent-detail-${g.id}`}>
      {periods > 0 && (
        <table className="w-full font-mono-num">
          <thead><tr className="text-slate-600"><td />{g.home.line.map((_, i) => <td key={i} className="text-center">{periodLabel(g.league, i)}</td>)}<td className="text-right">T</td></tr></thead>
          <tbody>
            {[g.away, g.home].map((t) => (
              <tr key={t.abbr} className={t.winner ? "text-white" : ""}><td>{t.abbr}</td>{t.line.map((v, i) => <td key={i} className="text-center">{v}</td>)}<td className="text-right font-bold">{t.score}</td></tr>
            ))}
          </tbody>
        </table>
      )}
      {g.goals?.length > 0 && (
        <div className="space-y-0.5" data-testid={`recent-goals-${g.id}`}>
          <div className="text-smasher-gold font-bold">GOALS</div>
          {g.goals.map((x, i) => <div key={i}><span className="font-mono-num text-slate-500 w-8 inline-block">{x.min}</span> <span className="text-white font-bold">{x.team}</span> {x.player}{x.tag ? ` (${x.tag})` : ""}</div>)}
        </div>
      )}
      {g.league !== "MLS" && <TopPerformers g={g} />}
      {g.venue && <div className="text-slate-600">{g.venue}</div>}
      <Link to={`/game/${g.league}/${g.id}`} onClick={(e) => e.stopPropagation()} className="inline-block text-smasher-gold font-black hover:text-smasher-orange" data-testid={`recent-boxscore-link-${g.id}`}>FULL BOX SCORE →</Link>
    </div>
  );
}

function GameRow({ g }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="w-full text-left py-2 border-b border-white/5 last:border-0" data-testid={`recent-result-${g.id}`}>
      <div role="button" tabIndex={0} onClick={() => setOpen(!open)} className="flex items-center gap-3 cursor-pointer">
        <div className="flex-1 space-y-0.5"><Side t={g.away} /><Side t={g.home} /></div>
        <div className="text-right shrink-0">
          <div className="text-[9px] font-bold text-slate-500">{g.status}</div>
          <div className="text-[9px] text-slate-600 font-mono-num">{new Date(g.date).toLocaleDateString("en-US", { month: "numeric", day: "numeric" })}</div>
          <ChevronDown className={`w-3 h-3 text-smasher-gold ml-auto transition-transform ${open ? "rotate-180" : ""}`} />
        </div>
      </div>
      {open && <Detail g={g} />}
    </div>
  );
}

export default function RecentResults() {
  const [league, setLeague] = useState("NFL");
  const [data, setData] = useState({});
  useEffect(() => {
    if (data[league] || league === "MMA") return;
    api.get("/recent-results", { params: { league } }).then((r) => setData((d) => ({ ...d, [league]: r.data.games || [] }))).catch(() => setData((d) => ({ ...d, [league]: [] })));
  }, [league, data]);
  const games = data[league];
  return (
    <div className="panel p-4" data-testid="recent-results">
      <div className="mb-3 space-y-2">
        <span className="flex items-center gap-2 font-heading font-bold tracking-wide text-smasher-gold text-sm"><History className="w-4 h-4" /> RECENT RESULTS</span>
        <div className="flex flex-wrap gap-1">
          {LEAGUES.map((l) => (
            <button key={l} onClick={() => setLeague(l)} className={`px-2 py-1 rounded-md text-[9px] font-black transition-colors ${league === l ? "btn-gold" : "bg-white/5 text-slate-400 hover:text-smasher-gold"}`} data-testid={`recent-tab-${l.toLowerCase()}`}>{l}</button>
          ))}
        </div>
      </div>
      <div className="max-h-[420px] overflow-auto no-scrollbar">
        {league === "MMA" && <MmaResults />}
        {league !== "MMA" && !games && <p className="text-xs text-slate-500 py-3">Loading…</p>}
        {league !== "MMA" && games && !games.length && <p className="text-xs text-slate-500 py-3" data-testid="recent-empty">No recent {league} finals.</p>}
        {league !== "MMA" && games?.map((g) => <GameRow key={g.id} g={{ ...g, league }} />)}
      </div>
      {league !== "MMA" && games?.length > 0 && <div className="text-[10px] text-slate-600 mt-2">Tap a game for the box score</div>}
    </div>
  );
}
